import {gql} from '@apollo/client';
import {naturalSortByFunction} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {Capability, RegistryFeature, ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import isCapabilityEnabledForTenant from 'auth/capabilities/isCapabilityEnabledForTenant';
import {updateDefaultSchemaCollaboratorsConfiguration} from 'auth/DefaultSchemaCollaboratorsConfigurationActions';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {getAdminSchemaPolicy} from 'auth/IntrinsicPolicies';
import {Collaborator, convertToCollaborators, convertToDefaultCollaborators} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DefaultSchemaCollaboratorsSection from 'collaborator-components/DefaultSchemaCollaboratorsSection';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import SidebarItem from 'sidebar/SidebarItem';
import getTestProps from 'test/lib/cypress/getTestProps';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {Header2} from 'ui-library/Headers';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize, listToReadable} from 'util/NameUtil';

import {DefaultSchemaCollaborationConfigurationsListingQuery} from './__generated/DefaultSchemaCollaborationConfigurationsListingQuery';

type Organization = NonNullable<
  DefaultSchemaCollaborationConfigurationsListingQuery['currentTenantOrganizations']
>[number];

export const DEFAULT_SCHEMA_COLLABORATION_CONFIGURATIONS_LISTING_QUERY = gql`
  query DefaultSchemaCollaborationConfigurationsListingQuery @skipAudit @engComponent(name: PERMISSIONS) {
    currentTenantOrganizations {
      id
      name
      schemaCollaborationDefaultConfigurations {
        ...DefaultSchemaCollaboratorsConfigurationGraphQL
      }
    }
  }
  ${getFragmentWrapper('DefaultSchemaCollaboratorsConfiguration').fragment}
`;

// Also change benchling/auth/permissions/models/default_configurations_enum.py:SCHEMA_TYPE_TO_DISPLAY
const SCHEMA_TYPE_TO_DISPLAY: {[key in DefaultSchemaCollaboratorsConfigurationSchemaType]: string} = {
  [DefaultSchemaCollaboratorsConfigurationSchemaType.ENTITY]: 'entity',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.RESULT]: 'result',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.RUN]: 'run',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.CONNECTION]: 'connection',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.INTERFACE]: 'fieldset',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.STUDY]: 'study',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.TEST_DEFINITION]: 'test_definition',
  [DefaultSchemaCollaboratorsConfigurationSchemaType.PROCESS_TASK]: 'workflow task',
};

function enabledSchemaTypes(): Array<DefaultSchemaCollaboratorsConfigurationSchemaType> {
  if (!isFeatureEnabled({feature: RegistryFeature.REGISTRY_ADMIN})) {
    return [];
  }
  const isLabAutoEnabled = isCapabilityEnabledForTenant(Capability.LAB_AUTOMATION);
  return [
    DefaultSchemaCollaboratorsConfigurationSchemaType.ENTITY,
    ...(BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS &&
    isFeatureEnabled({feature: ResultsFeature.RESULTS_ADMIN})
      ? [DefaultSchemaCollaboratorsConfigurationSchemaType.RESULT]
      : []),
    ...(BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS && isLabAutoEnabled
      ? [DefaultSchemaCollaboratorsConfigurationSchemaType.RUN]
      : []),
    // Connection default schema access policies are intentionally omitted: the CONNECTION config
    // has no consumer (connection creation permissions its instances as ownables, and connection
    // schemas are being removed), so surfacing it here misled admins into thinking it governed
    // connection permissions. See BNCH-149217.
    DefaultSchemaCollaboratorsConfigurationSchemaType.INTERFACE,
    ...(BenchlingConfig.STUDIES_ENABLE ? [DefaultSchemaCollaboratorsConfigurationSchemaType.STUDY] : []),
    ...(BenchlingConfig.WORKFLOWS_ENABLE
      ? [DefaultSchemaCollaboratorsConfigurationSchemaType.PROCESS_TASK]
      : []),
    // TODO(PLAN-1278): Opting to not include TestDefinitions in the default schema permissions settings until product need
  ];
}

interface OrganizationSettingsProps {
  organizations: Array<Organization>;
}

function OrganizationSettingsSection(props: OrganizationSettingsProps): JSX.Element {
  return (
    <div
      className="u-spacing-top"
      {...getTestProps({component: 'SchemaPoliciesSettingsTab', element: 'organizationSection'})}
    >
      <Header2>Organization</Header2>
      {(() =>
        props.organizations.length === 0 ? (
          <SelectDropdown menu={<Menu />} disabled={true} displayName="No organizations" />
        ) : (
          <OrganizationDropdownAndContent {...props} />
        ))()}
    </div>
  );
}

function OrganizationDropdownAndContent(props: OrganizationSettingsProps): JSX.Element {
  const [organization, setOrganization] = React.useState(props.organizations[0]);
  return (
    <>
      <SelectDropdown
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={organization}>
            {props.organizations.map((organization) => (
              <Option key={organization.id} value={organization} onSelect={setOrganization}>
                {organization.name}
              </Option>
            ))}
          </Menu>
        }
        displayName={organization.name}
      />
      <OrganizationContent organization={organization} />
    </>
  );
}

function getEnabledSchemaTypesForOrg(
  organization: Organization
): Array<DefaultSchemaCollaboratorsConfigurationSchemaType> {
  return organization.schemaCollaborationDefaultConfigurations
    .map((defaultConfig) => defaultConfig.schemaType)
    .filter((schemaType) => enabledSchemaTypes().includes(schemaType));
}

function getDefaultSchemaTypeForOrg(
  organization: Organization
): DefaultSchemaCollaboratorsConfigurationSchemaType | undefined {
  const schemaTypes = getEnabledSchemaTypesForOrg(organization);
  return schemaTypes.length > 0 ? schemaTypes[0] : undefined;
}
interface OrganizationContentProps {
  organization: Organization;
}

export function OrganizationContent(props: OrganizationContentProps): JSX.Element {
  const [activeSchemaType, setActiveSchemaType] = React.useState(
    getDefaultSchemaTypeForOrg(props.organization)
  );
  React.useEffect(() => {
    setActiveSchemaType(getDefaultSchemaTypeForOrg(props.organization));
  }, [props.organization]);

  if (activeSchemaType == null) {
    return <div>No eligible schema types.</div>;
  }

  return (
    <div
      className={classNames(css(styles.container))}
      {...getTestProps({component: 'SchemaPoliciesSettingsTab', element: 'organizationContent'})}
    >
      <div className={css(styles.sidebarContainer)}>
        <SchemaTypeSidebar
          activeSchemaType={activeSchemaType}
          schemaTypes={getEnabledSchemaTypesForOrg(props.organization)}
          onClick={setActiveSchemaType}
        />
      </div>
      <div className={css(styles.collaboratorSectionContainer)}>
        <CollaboratorsSection organization={props.organization} activeSchemaType={activeSchemaType} />
      </div>
    </div>
  );
}

function CollaboratorsSection(props: {
  organization: Organization;
  activeSchemaType: DefaultSchemaCollaboratorsConfigurationSchemaType;
}): JSX.Element {
  const [isLoading, setIsLoading] = React.useState(false);
  const [configuration, setConfiguration] = React.useState(
    props.organization.schemaCollaborationDefaultConfigurations.find(
      (c) => c.schemaType === props.activeSchemaType
    )
  );
  const [defaultCollaborators, setDefaultCollaborators] = React.useState(
    convertToCollaborators(configuration, props.organization)
  );
  const [saveButtonDisabled, setSaveButtonDisabled] = React.useState(true);
  React.useEffect(() => {
    const configuration = props.organization.schemaCollaborationDefaultConfigurations.find(
      (c) => c.schemaType === props.activeSchemaType
    );
    if (configuration == null) {
      return;
    }
    const cachedConfig = readFragmentFromCache(
      getFragmentWrapper('DefaultSchemaCollaboratorsConfiguration'),
      configuration.id
    )!;
    setConfiguration(cachedConfig);
    setDefaultCollaborators(convertToCollaborators(cachedConfig, props.organization));
    setSaveButtonDisabled(true);
    setIsLoading(false);
  }, [props.activeSchemaType, props.organization]);

  function _handleDefaultCollaboratorsUpdate(collaborators: Array<Collaborator>): void {
    setSaveButtonDisabled(false);
    setDefaultCollaborators(collaborators);
  }
  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }
  if (configuration == null) {
    return <div>No eligible schema types.</div>;
  }
  return (
    <DefaultSchemaCollaboratorsSection
      activeSchemaType={props.activeSchemaType}
      collaborators={defaultCollaborators}
      permissions={configuration.permissions}
      onUpdate={_handleDefaultCollaboratorsUpdate}
      onSubmit={async () => {
        setIsLoading(true);
        const defaultCollaboratorConfigs = convertToDefaultCollaborators(defaultCollaborators);
        await updateDefaultSchemaCollaboratorsConfiguration(configuration.id, {
          collaborations: defaultCollaboratorConfigs,
        });
        setConfiguration(
          readFragmentFromCache(
            getFragmentWrapper('DefaultSchemaCollaboratorsConfiguration'),
            configuration.id
          )!
        );
        setIsLoading(false);
      }}
      getAdminSchemaPolicy={getAdminSchemaPolicy}
      saveButtonDisabled={saveButtonDisabled}
    />
  );
}

interface SchemaTypeSidebarProps {
  activeSchemaType: DefaultSchemaCollaboratorsConfigurationSchemaType;
  schemaTypes: Array<DefaultSchemaCollaboratorsConfigurationSchemaType>;
  onClick: (schemaType: DefaultSchemaCollaboratorsConfigurationSchemaType) => void;
}

export function SchemaTypeSidebar(props: SchemaTypeSidebarProps): JSX.Element {
  const sortedSchemaTypes = naturalSortByFunction(
    props.schemaTypes,
    (schemaType) => SCHEMA_TYPE_TO_DISPLAY[schemaType]
  );
  return (
    <>
      {sortedSchemaTypes.map((schemaType: DefaultSchemaCollaboratorsConfigurationSchemaType) => (
        <SidebarItem
          key={schemaType}
          active={props.activeSchemaType === schemaType}
          icon={React.createElement('span')}
          name={capitalize(SCHEMA_TYPE_TO_DISPLAY[schemaType])}
          clickHandler={() => {
            props.onClick(schemaType);
          }}
        />
      ))}
    </>
  );
}

interface SchemaPoliciesSettingsTabProps {
  organizations: Array<Organization>;
}

export function SchemaPoliciesSettingsTab(props: SchemaPoliciesSettingsTabProps): JSX.Element {
  const enabledSchemaTypesDisplay = enabledSchemaTypes().map(
    (schemaType) => SCHEMA_TYPE_TO_DISPLAY[schemaType]
  );
  return (
    <div {...getTestProps({component: 'SchemaPoliciesSettingsTab', element: 'root'})}>
      <Header2>Default Schema Access Policy Options</Header2>
      <p>
        Configure the default schema access policy options when any new{' '}
        {listToReadable(enabledSchemaTypesDisplay, 'or').toLowerCase()} schemas are created in this
        organization. Default options will only be shown when creating new schemas.
      </p>
      <OrganizationSettingsSection organizations={props.organizations} />
    </div>
  );
}

export default function QueryingSchemaPoliciesSettingsTab(): JSX.Element {
  return (
    <BenchlingQuery<DefaultSchemaCollaborationConfigurationsListingQuery>
      query={DEFAULT_SCHEMA_COLLABORATION_CONFIGURATIONS_LISTING_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => <SchemaPoliciesSettingsTab organizations={data.currentTenantOrganizations ?? []} />}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    height: '100%',
    marginTop: 15,
  },
  containerRow: {
    height: '100%',
  },
  collaboratorSectionContainer: {
    marginLeft: 15,
  },
  sidebarContainer: {
    padding: '0 15px',
    minWidth: 170,
  },
});
